// Session-label suffix for a loaded replay: the score's mods (colloquial order) with any lazer
// mod settings, then the accuracy implied by the .osr header counts.

import { activeModAcronyms, extendedModIconInfo, type LazerMod, type ReplayData } from '../lib/index.js';

// The order players write mod combos in ("HDDT", "HDDTHR", "EZHDDT", "HDHRFL"). This deliberately
// diverges from lazer, which sorts by mod type then acronym ("DTHDHR"); the HUD icon row keeps its
// own order (`MOD_ICON_SPECS`).
const LABEL_MOD_ORDER: readonly string[] = [
  'NF', 'EZ', 'HD', 'DT', 'NC', 'HR', 'SD', 'PF', 'HT', 'DC', 'RX', 'FL', 'SO',
  'CL', 'DA', 'AC', 'FI', 'MR', 'CO',
];
const LABEL_MOD_RANK = new Map(LABEL_MOD_ORDER.map((acronym, i) => [acronym, i]));

// DA settings shown after the mod block, in this order ("AR8 CS7").
const DA_SETTINGS: readonly [key: string, label: string][] = [
  ['approach_rate', 'AR'], ['circle_size', 'CS'], ['overall_difficulty', 'OD'], ['drain_rate', 'HP'],
];

/**
 * Acronyms of the score's mods (the same set the HUD icon row shows — see `activeModAcronyms`)
 * in colloquial order (`LABEL_MOD_ORDER`).
 */
export function orderedModAcronyms(mods: number, replay: ReplayData): string[] {
  return activeModAcronyms(mods, replay.scoreInfo?.mods)
    .sort((a, b) => (LABEL_MOD_RANK.get(a) ?? Infinity) - (LABEL_MOD_RANK.get(b) ?? Infinity));
}

/**
 * The settings a lazer mod was played with, as they appear after the mod block: rate mods at
 * a non-default `speed_change` give `1.35x` (via `extendedModIconInfo`); DA lists every
 * changed difficulty setting (`AR8 CS7`, at most one decimal). `''` when nothing to show.
 */
export function modSettingsText(mod: LazerMod): string {
  if (mod.acronym !== 'DA') return extendedModIconInfo(mod);
  const s = mod.settings;
  if (s === undefined) return '';
  return DA_SETTINGS
    .filter(([key]) => typeof s[key] === 'number')
    .map(([key, label]) => `${label}${(Math.round((s[key] as number) * 10) / 10).toString()}`)
    .join(' ');
}

/** Accuracy (0..1) from the .osr header counts, using each ruleset's stable formula. */
export function headerAccuracy(r: ReplayData): number {
  const { count300: c300, count100: c100, count50: c50, countGeki: geki, countKatu: katu, countMiss: miss } = r;
  switch (r.mode) {
    case 1: { // taiko: 100 = good (half weight)
      const total = c300 + c100 + miss;
      return total === 0 ? 1 : (c300 + c100 * 0.5) / total;
    }
    case 2: { // catch: 300/100/50 = fruit/drop/droplet caught, katu = droplet missed
      const total = c300 + c100 + c50 + miss + katu;
      return total === 0 ? 1 : (c300 + c100 + c50) / total;
    }
    case 3: { // mania: geki = MAX, katu = 200
      const total = geki + c300 + katu + c100 + c50 + miss;
      return total === 0 ? 1 : (300 * (geki + c300) + 200 * katu + 100 * c100 + 50 * c50) / (300 * total);
    }
    default: {
      const total = c300 + c100 + c50 + miss;
      return total === 0 ? 1 : (300 * c300 + 100 * c100 + 50 * c50) / (300 * total);
    }
  }
}

/** `98.76%`. Floored to two decimals like lazer's `FormatAccuracy`, so 99.999% never reads as 100%. */
export function formatAccuracy(accuracy: number): string {
  return `${(Math.floor(accuracy * 10000 + 1e-6) / 100).toFixed(2)}%`;
}

/**
 * ` +HDHR 98.76%`. Lazer mod settings follow the mod block (` +HDDT 1.35x AR8 CS7 98.76%`);
 * a DA whose settings are all listed drops its own acronym. No mod block when the score is nomod.
 */
export function replayLabelSuffix(mods: number, replay: ReplayData): string {
  const lazerMods = replay.scoreInfo?.mods ?? [];
  const shown: string[] = [];
  const settings: string[] = [];
  for (const acronym of orderedModAcronyms(mods, replay)) {
    const mod = lazerMods.find(m => m.acronym === acronym);
    const text = mod === undefined ? '' : modSettingsText(mod);
    if (text !== '') settings.push(text);
    if (text === '' || acronym !== 'DA') shown.push(acronym);
  }
  const modText = shown.length > 0 ? ` +${shown.join('')}` : '';
  const settingsText = settings.length > 0 ? ` ${settings.join(' ')}` : '';
  return `${modText}${settingsText} ${formatAccuracy(headerAccuracy(replay))}`;
}
